import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { ChangeDateFormat } from "../../Common/Helpers/ChangeDateFormat";
import { useNavigate, useLocation } from "react-router-dom";
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import { ADMIN_PATHS } from "../../config";
import ResponseUserModel from "../../User/Models/Response/ResponseUserModel";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import ResponseOrgModel from "../../Org/Models/Response/ResponseOrgModel";
import { useTranslation } from "react-i18next";
import { CheckDecimal } from "../../Common/Helpers/CheckDecimal";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import CostBearerModel from "../../CostBearer/Models/Response/CostBearerModel";
import { CustomSubmitButton } from "../../Common/Components/Input/CustomSubmitButton";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { CheckUserRole } from "../../AppSystem/Helpers/CheckUserRole";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { ToCustomDecimal } from "../../AppSystem/Helpers/ToCustomDecimal";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";

const AdminCalendarActivityInfo = () => {
  const isSuperAdmin = CheckUserRole("SuperAdmin");
  const { t } = useTranslation();
  const { ApiCall } = useApiCallService();
  const [activityInfo, setActivityInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const idValue = new URLSearchParams(window.location.search).get("id");
  const navigate = useNavigate();

  const isMobile = CheckMobile(998);
  const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

  useEffect(() => {
        setPageTitle(t('modelLabel.activityCalendarDetails'));
        setPageTitleKey('modelLabel.activityCalendarDetails');
        setOnBack(() => navigate(ADMIN_PATHS.ADMIN_CALENDAR_ACTIVITY_LIST));
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

  useEffect(() => {
    const handleApiResponseGetActivityCalendar = (
      apiResponseActivityCalendar,
    ) => {
      setActivityInfo(apiResponseActivityCalendar);
    };
    const GetActivityCalendar = async () => {
      try {
        const apiCallOptions = {
          method: "get",
          url: `/roten/admin?id=${idValue}`,
        };

        await ApiCall(apiCallOptions, handleApiResponseGetActivityCalendar);
      } catch (error) {
        console.log("error", error);
      }
    };

    GetActivityCalendar();
  }, []);

  if (!activityInfo)
    return (
      <div className="p-5 text-center">{t("sortableList.gettingData")}...</div>
    );

  return (
    <>
      {!isMobile && (
        <Col lg={1} className="px-0">
          <CustomButton
            text={t("button.back")}
            icon={"bx bx-chevron-left"}
            buttonSize={"small"}
            customClasses={"m-3 rounded"}
            onClick={() => navigate(ADMIN_PATHS.ADMIN_CALENDAR_ACTIVITY_LIST)}
          />
        </Col>
      )}

      <Col lg="12" className="card card-backend p-4 mb-5">
        <Row>
          <Col md={6} className="mb-4">
            <h5 className="text-muted border-bottom pb-2">
              {t("modelLabel.activityDetails")}
            </h5>
            <ul className="list-unstyled">
              <li className="pb-2">
                <strong>{t("modelLabel.activityId")}:</strong>{" "}
                {activityInfo.activityId}
              </li>
              <li className="pb-2">
                <strong>{t("modelLabel.activityName")}:</strong>{" "}
                {activityInfo.activityName}
              </li>
              <li className="pb-2">
                <strong>{t("modelLabel.activityOrganizer")}:</strong>{" "}
                {activityInfo.organizer}
              </li>
              <li className="pb-2">
                <strong>{t("modelLabel.activityType")}:</strong>{" "}
                {activityInfo.activityTypeName}
              </li>
              <li className="pb-2">
                <strong>{t("modelLabel.activityOrganizerRegion")}:</strong>{" "}
                {activityInfo.organizerRegion}
              </li>
              <li className="pb-2">
                <strong>{t("modelLabel.activityLocation")}:</strong>{" "}
                {activityInfo.location}
              </li>
              <li className="pb-2">
                <strong>
                  {t("modelLabel.activityRegistrationRequirement")}:
                </strong>{" "}
                {activityInfo.registrationRequirement}
              </li>
              <li className="pb-2">
                <strong>{t("modelLabel.activityTargetGroup")}:</strong>{" "}
                {activityInfo.TargetGroup}
              </li>
            </ul>
          </Col>

          <Col md={6} className="mb-4">
            <h5 className="text-muted border-bottom pb-2">
              {t("modelLabel.activityDates")}
            </h5>
            <ul className="list-unstyled">
              {activityInfo.datetimeActivityFrom && (
                <li className="pb-2">
                  <strong>{t("modelLabel.activityfromDate")}:</strong>{" "}
                  {ChangeDateFormat(
                    "dateTime",
                    activityInfo.datetimeActivityFrom,
                  )}
                </li>
              )}

              {activityInfo.datetimeActivityTo && (
                <li className="pb-2">
                  <strong>{t("modelLabel.activitytoDate")}:</strong>{" "}
                  {ChangeDateFormat(
                    "dateTime",
                    activityInfo.datetimeActivityTo,
                  )}
                </li>
              )}

              {activityInfo.dateChangedSinceFrom && (
                <li className="pb-2">
                  <strong>{t("modelLabel.updatedAt")}:</strong>{" "}
                  {ChangeDateFormat(
                    "dateTime",
                    activityInfo.dateChangedSinceFrom,
                  )}
                </li>
              )}

              {activityInfo.createdAt && (
                <li className="pb-2">
                  <strong>{t("modelLabel.createdAt")}:</strong>{" "}
                  {ChangeDateFormat("dateTime", activityInfo.createdAt)}
                </li>
              )}
            </ul>
          </Col>

          {((activityInfo.MaximumNumberOfParticipants !== null &&
            activityInfo.MaximumNumberOfParticipants > 0) ||
            (activityInfo.MinimumNumberOfParticipants !== null &&
              activityInfo.MinimumNumberOfParticipants > 0)) && (
            <Col md={6} className="mb-4">
              <h5 className="text-muted border-bottom pb-2">
                {t("modelLabel.activityPrices")}
              </h5>
              <ul className="list-unstyled">
                {activityInfo.MaximumNumberOfParticipants !== null &&
                  activityInfo.MaximumNumberOfParticipants > 0 && (
                    <li>
                      <strong>
                        {t("modelLabel.activityMaximumNumberOfParticipants")}
                        :{" "}
                      </strong>
                      <span className="text-success fw-bold">
                        {activityInfo.MaximumNumberOfParticipants}
                      </span>
                    </li>
                  )}

                {activityInfo.MinimumNumberOfParticipants !== null &&
                  activityInfo.MinimumNumberOfParticipants > 0 && (
                    <li
                      className={
                        activityInfo.MinimumNumberOfParticipants > 0
                          ? "mt-2"
                          : ""
                      }
                    >
                      <strong>
                        {t("modelLabel.activityMinimumNumberOfParticipants")}
                        :{" "}
                      </strong>
                      <span>{activityInfo.MinimumNumberOfParticipants}</span>
                    </li>
                  )}
              </ul>
            </Col>
          )}

          {((activityInfo.priceForMember !== null &&
            activityInfo.priceForMember > 0) ||
            (activityInfo.priceForNonMember !== null &&
              activityInfo.priceForNonMember > 0)) && (
            <Col md={6} className="mb-4">
              <h5 className="text-muted border-bottom pb-2">
                {t("modelLabel.activityPrices")}
              </h5>
              <ul className="list-unstyled">
                {activityInfo.priceForMember !== null &&
                  activityInfo.priceForMember > 0 && (
                    <li>
                      <strong>{t("modelLabel.activityPriceMember")}: </strong>
                      <span className="text-success fw-bold">
                        {ToCustomDecimal(activityInfo.priceForMember)} kr
                      </span>
                    </li>
                  )}

                {activityInfo.priceForNonMember !== null &&
                  activityInfo.priceForNonMember > 0 && (
                    <li
                      className={activityInfo.priceForMember > 0 ? "mt-2" : ""}
                    >
                      <strong>
                        {t("modelLabel.activityPriceNonMember")}:{" "}
                      </strong>
                      <span>
                        {ToCustomDecimal(activityInfo.priceForNonMember)} kr
                      </span>
                    </li>
                  )}
              </ul>
            </Col>
          )}

          {activityInfo.calendarText &&
            activityInfo.calendarText.trim() !== "" && (
              <Col md={12} className="mt-4">
                <h5 className="text-muted border-bottom pb-2">
                  {t("modelLabel.description")}
                </h5>
                <div
                  className="p-3 bg-light rounded"
                  dangerouslySetInnerHTML={{
                    __html: activityInfo.calendarText,
                  }}
                />
              </Col>
            )}
        </Row>
      </Col>
    </>
  );
};

export default AdminCalendarActivityInfo;
